import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { cloneDeep, isEqual, sortBy } from 'lodash';
import { Button, Form, FormSection, FormInput, Dropdown } from '@praktio/common-ui';
import { Form as RBForm, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet';

import {
  addModule,
  clearModule,
  getModule,
  updateModule,
  getAuthorModules,
  deleteModule,
  queueTranscription,
} from '../../ducks/modules';
import { RootState } from '../../ducks/store';
import ActionBar from '../common/ActionBar';
import FormField from '../common/FormField';
import { clearCourse, getCourse } from '../../ducks/courses';
import { ModuleTypes } from '../../utils/constants';
import Player from '../common/Player';
import DocViewer from '../common/DocViewer';
import EmbeddedViewer from '../common/EmbeddedViewer';
import DeleteModal from './modals/DeleteModal/DeleteModal';

import './Module.scss';
import classNames from 'classnames';

const ModuleTypeOptions = Object.keys(ModuleTypes).map((k) => ({ label: k, id: ModuleTypes[k] }));

const AuthorFields = ({
  module,
  authorModules,
  updateData,
}: {
  module: IModule;
  authorModules?: IModule[];
  updateData: (prop: string, value: any) => void;
}) => {
  const options = [
    ...sortBy(
      (authorModules || []).map(
        (m) => ({
          label: `${m.courseName} - ${m.title}`,
          id: m._id as string,
        }),
        'title',
      ),
    ),
  ];
  return (
    <Dropdown
      id="author-module"
      label="Author Module"
      options={options}
      onSelect={(id) => updateData('authorId', id)}
      value={options.find((o) => o.id === module.authorId)}
      formComponent
    />
  );
};

const VideoFields = ({
  module,
  onFileChange,
  onQueueTranscription,
  onCaptionsChange,
}: {
  module: IModule;
  onFileChange: (files?: FileList | null) => void;
  onQueueTranscription: () => void;
  onCaptionsChange: (value: string) => void;
}) => {
  const video = module?.videoFile || module?.video;
  const url = module?.video?.url;
  const captions = module?.video?.captions;
  const captionsQueued = module?.video?.captionsQueued;
  return (
    <>
      <FormInput
        label="Video File"
        secondaryButton
        type="file"
        onChange={(e: any) => onFileChange(e.target.files)}
        fileSelectButtonText={!!video ? 'Replace' : 'Upload'}
        singleLine
        noPreview
        accept="video/*"
      >
        {!!video && (
          <>
            <Button onClick={() => onFileChange()} className="secondary">
              Clear
            </Button>
            <div className="file-data">
              <div>Url: {url}</div>
            </div>
          </>
        )}
      </FormInput>
      {!!video && (
        <div className={classNames('Module-preview', { 'has-video': !!video })}>
          <div className="preview-side">
            <h3>Preview</h3>
            <Player disableAutoPlay data={video} type="VIDEO" ignoreKeyPress />
          </div>
          <div className="captions-side">
            <div className="captions-header">
              <h3>Captions</h3>
              <Button
                disabled={!url || !!captions || captionsQueued}
                onClick={onQueueTranscription}
              >
                <i className="material-icons">comment</i>
                <span>Queue Transcription</span>
              </Button>
            </div>
            <textarea
              value={captionsQueued ? 'Queued...' : captions || ''}
              onChange={(e) => {
                if (!captionsQueued) {
                  onCaptionsChange(e.target.value);
                }
              }}
            ></textarea>
          </div>
        </div>
      )}
    </>
  );
};

const DocumentFields = ({
  module,
  onPDFFileChange,
  onWordFileChange,
}: {
  module: IModule;
  onPDFFileChange: (files?: FileList | null) => void;
  onWordFileChange: (files?: FileList | null) => void;
}) => {
  const pdfDoc = module?.pdfFile || module?.document;
  let pdfSourceUrl;
  if (pdfDoc) {
    pdfSourceUrl = !(pdfDoc as any).url
      ? URL.createObjectURL(pdfDoc as File)
      : (pdfDoc as IDocument).url;
  }

  const wordDoc = module?.wordFile || module?.document;
  const wordName = module?.wordFile ? wordDoc?.name : wordDoc?.wordName;
  const wordUrl = module?.wordFile ? wordDoc?.url : wordDoc?.wordUrl;
  return (
    <>
      <FormInput
        fileSource={pdfSourceUrl}
        label="PDF File"
        secondaryButton
        type="file"
        onChange={(e: any) => onPDFFileChange(e.target.files)}
        fileSelectButtonText={pdfSourceUrl ? 'Replace' : 'Upload'}
        singleLine
        previewComponent={
          <DocViewer document={pdfDoc as IDocument} className="DocViewer-preview" />
        }
        accept=".pdf"
      >
        {pdfSourceUrl && (
          <>
            <Button onClick={() => onPDFFileChange()} className="secondary">
              Clear
            </Button>
            <div className="file-data">
              <div>Name: {pdfDoc?.name}</div>
              <div>Url: {(pdfDoc as any)?.url}</div>
            </div>
          </>
        )}
      </FormInput>
      {pdfDoc && (
        <FormInput
          label="Word File"
          secondaryButton
          type="file"
          onChange={(e: any) => onWordFileChange(e.target.files)}
          fileSelectButtonText={wordName ? 'Replace' : 'Upload'}
          singleLine
          noPreview
          accept=".doc,.docx"
        >
          {wordName && (
            <>
              <Button onClick={() => onWordFileChange()} className="secondary">
                Clear
              </Button>
              <div className="file-data">
                <div>Name: {wordName}</div>
                <div>Url: {wordUrl}</div>
              </div>
            </>
          )}
        </FormInput>
      )}
    </>
  );
};

const EmbeddedFields = ({ module, onChange }: { module: IModule; onChange: (e: any) => void }) => {
  const embeddedData = module?.embedded;
  return (
    <>
      <FormInput
        fieldName="html"
        label="HTML"
        onChange={onChange}
        value={embeddedData?.html || ''}
        type="textarea"
      />
      <div className="Module-preview">
        <h3>Preview</h3>
        <EmbeddedViewer data={embeddedData} />
      </div>
    </>
  );
};

const initialModule = () => ({ type: ModuleTypes.Author } as IModule);

const Module = () => {
  const navigate = useNavigate();
  const params = useParams();
  const courseId = params.courseId as string;
  const moduleId = params.moduleId as string;
  const isNew = moduleId === 'new';
  const dispatch = useDispatch();
  const { data, course, authorModules } = useSelector((state: RootState) => {
    const { current: data, authorModules } = state.modules;
    return {
      data,
      authorModules,
      course: state.courses.current,
    };
  });

  const prevModule = useRef<IModule | undefined>();
  const [module, setModule] = useState(data || initialModule());
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (!course) {
      dispatch(getCourse({ courseId }));
    }
  }, [course, courseId, dispatch]);

  useEffect(() => {
    if (moduleId !== data?._id) {
      if (!isNew) {
        dispatch(getModule({ courseId, moduleId }));
      } else {
        const newModule: IModule = initialModule();
        setModule(newModule);
      }

      dispatch(getAuthorModules());
    }

    if (data && !isEqual(prevModule.current, data)) {
      setModule(data);
    }

    prevModule.current = cloneDeep(data);
  }, [data, isNew, courseId, moduleId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearModule());
      dispatch(clearCourse());
    };
  }, [dispatch]);

  const updateData = (prop: string, value: any) => {
    const updatedModule = cloneDeep(module);
    updatedModule[prop] = value;
    if (prop === 'authorId' && value && value !== '') {
      delete updatedModule.videoFile;
      delete updatedModule.video;
      delete updatedModule.pdfFile;
      delete updatedModule.wordFile;
      delete updatedModule.document;
      delete updatedModule.embedded;

      const authorModule = authorModules?.find((m) => m._id === value);
      updatedModule.disableRetake = authorModule?.isPre || authorModule?.isPost;
    }

    setModule(updatedModule);
  };

  const onChange = (prop: string, e: any) => {
    const value = e.target.value;
    updateData(prop, value);
  };

  const onCaptionsChange = (value: string) => {
    const updatedModule = cloneDeep(module);
    if (updatedModule.video) {
      updatedModule.video.captions = value;
      setModule(updatedModule);
    }
  };

  const onVideoFileChange = (files?: FileList | null) => {
    const updatedModule = cloneDeep(module);
    delete updatedModule.authorId;
    delete updatedModule.pdfFile;
    delete updatedModule.wordFile;
    delete updatedModule.document;
    delete updatedModule.embedded;
    updatedModule.videoFile = files ? files[0] : undefined;
    if (!updatedModule.videoFile) {
      updatedModule.video = undefined;
    }

    setModule(updatedModule);
  };

  const onPDFFileChange = (files?: FileList | null) => {
    const updatedModule = cloneDeep(module);
    delete updatedModule.authorId;
    delete updatedModule.video;
    delete updatedModule.videoFile;
    delete updatedModule.embedded;
    updatedModule.pdfFile = files ? files[0] : undefined;
    if (!updatedModule.pdfFile) {
      updatedModule.document = undefined;
    }

    setModule(updatedModule);
  };

  const onWordFileChange = (files?: FileList | null) => {
    const updatedModule = cloneDeep(module);
    delete updatedModule.authorId;
    delete updatedModule.video;
    delete updatedModule.videoFile;
    delete updatedModule.embedded;
    if (updatedModule.document) {
      delete updatedModule.document.wordName;
      delete updatedModule.document.wordUrl;
    }

    updatedModule.wordFile = files ? files[0] : undefined;
    setModule(updatedModule);
  };

  const onEmbeddedHtmlChange = (value: string) => {
    const updatedModule = cloneDeep(module);
    delete updatedModule.authorId;
    delete updatedModule.video;
    delete updatedModule.videoFile;
    delete updatedModule.pdfFile;
    delete updatedModule.wordFile;
    delete updatedModule.document;

    updatedModule.embedded = { html: value };
    setModule(updatedModule);
  };

  const onQueueTranscription = () => {
    dispatch(queueTranscription({ courseId, module }));
  };

  let typeSpecificFields;
  switch (module.type) {
    case ModuleTypes.Author:
      typeSpecificFields = (
        <AuthorFields module={module} authorModules={authorModules} updateData={updateData} />
      );
      break;
    case ModuleTypes.Video:
      typeSpecificFields = (
        <VideoFields
          module={module}
          onFileChange={onVideoFileChange}
          onQueueTranscription={onQueueTranscription}
          onCaptionsChange={onCaptionsChange}
        />
      );
      break;
    case ModuleTypes.Document:
      typeSpecificFields = (
        <DocumentFields
          module={module}
          onPDFFileChange={onPDFFileChange}
          onWordFileChange={onWordFileChange}
        />
      );
      break;
    case ModuleTypes.Embedded:
      typeSpecificFields = (
        <EmbeddedFields
          module={module}
          onChange={(e: any) => onEmbeddedHtmlChange(e.target.value)}
        />
      );
      break;
    default:
      break;
  }

  const hasChanges = !isEqual(module, prevModule.current);
  return (
    <div className="Module">
      <Helmet>
        <title>{module?.title || 'Module'} - Praktio</title>
      </Helmet>
      <ActionBar hasChanges={hasChanges}>
        <Button
          disabled={!module.title || module.title === '' || !hasChanges}
          onClick={(e) => {
            e?.preventDefault();
            if (module._id) {
              dispatch(updateModule({ courseId, module }));
            } else {
              dispatch(addModule({ courseId, module, navigate }));
            }
          }}
        >
          SAVE
        </Button>
      </ActionBar>
      <Form formId="admin-module-form" onSubmit={() => {}}>
        <FormField labelName="Course">
          <div>
            <Link to={`/admin/courses/${course?._id}`}>{course?.title}</Link>
          </div>
        </FormField>
        <FormSection sectionName="Module Information">
          <FormInput
            label="Published"
            fieldName="published"
            onChange={() => updateData('published', !module.published)}
            value={module.published || false}
            type="switch"
          />
          <FormInput
            label="Title"
            fieldName="title"
            onChange={(e: any) => onChange('title', e)}
            value={module.title}
            required
          />
          <FormInput
            label="Instructions"
            fieldName="instructions"
            onChange={(e: any) => onChange('instructions', e)}
            value={module.instructions || ''}
            type="textarea"
          />
          <FormInput
            label="Tooltip Text"
            fieldName="tooltipText"
            onChange={(e: any) => onChange('tooltipText', e)}
            value={module.tooltipText || ''}
            type="textarea"
          />
        </FormSection>
        <FormSection sectionName="Module Settings">
          <Dropdown
            id="module-type"
            label="Module Type"
            options={ModuleTypeOptions}
            onSelect={(id) => updateData('type', id)}
            value={ModuleTypeOptions.find((mt) => mt.id === module.type)}
            formComponent
          />
          {typeSpecificFields}
          <FormInput
            label="Is Optional Module"
            fieldName="isOptional"
            onChange={() => updateData('isOptional', !module.isOptional)}
            value={module.isOptional || false}
            type="switch"
          />
          <FormInput
            label="Prevent Retakes"
            fieldName="disableRetake"
            onChange={() => updateData('disableRetake', !module.disableRetake)}
            value={module.disableRetake || false}
            type="switch"
          />
          <FormInput
            label="Take Anytime"
            fieldName="takeAnytime"
            onChange={() => updateData('takeAnytime', !module.takeAnytime)}
            value={module.takeAnytime || false}
            type="switch"
          />
          <FormInput
            label="Time Release (in days)"
            fieldName="timeReleasePeriod"
            type="number"
            onChange={(e: any) => onChange('timeReleasePeriod', e)}
            value={module.timeReleasePeriod}
            numMin={0}
          />
        </FormSection>
        <FormSection
          sectionName="Delete Module"
          sectionDescription="Permanently delete this module from Praktio. This cannot be undone."
          sectionAddition={
            <Button onClick={() => setShowDeleteModal(true)} className="secondary">
              DELETE
            </Button>
          }
        />
      </Form>
      <DeleteModal
        type="Module"
        name={module.title || ''}
        onHide={() => setShowDeleteModal(false)}
        show={showDeleteModal}
        dispatchAction={deleteModule}
        dispatchData={{ courseId, moduleId }}
      />
    </div>
  );
};

export default Module;
